import {useCallback, useEffect} from 'react';
import {CartProvider as ShopifyCartProvider, useCart} from '@shopify/hydrogen';
import {useCartUI, useUrlStateParam} from '../hooks';
import {CART_FRAGMENT} from '../shopifyGraphql/cart';

const bespokeKey = '_bespokeid';

export default function CartProvider({children, numCartLines}) {
  const {openCart} = useCartUI();

  const open = useCallback(() => {
    openCart();
  }, [openCart]);

  return (
    <ShopifyCartProvider
      numCartLines={numCartLines}
      onLineAdd={open}
      cartFragment={CART_FRAGMENT}
    >
      <_CreateCardOnLoad>{children}</_CreateCardOnLoad>
    </ShopifyCartProvider>
  );
}

function _CreateCardOnLoad({children}) {
  const {
    cartCreate,
    id,
    status,
    discountCodesUpdate,
    discountCodes,
    lines,
    linesUpdate,
  } = useCart();

  const {value: discountCode, unsetValue: unsetDiscountCode} = useUrlStateParam(
    {param: 'code'},
  );

  useEffect(() => {
    // For the Bespoke Shipping app to accurately calculate shipping
    // rates, we need to set the _bespokeid attribute with the cart id,
    // on only _ONE_ line item, and that line item must be the one that
    // requires shipping.
    const withPropertySet: typeof lines = [];
    const requiresShipping: typeof lines = [];
    for (const line of lines) {
      if (line.merchandise.requiresShipping) {
        requiresShipping.push(line);
      }
      if (line.attributes.find(({key}) => key === bespokeKey)) {
        withPropertySet.push(line);
      }
    }
    const linesToUpdate = [];
    if (withPropertySet.length > 1) {
      // there are multiple lines with the bespokeKey, we need to remove
      // the attributes from the excess lines
      linesToUpdate.push(
        ...withPropertySet.slice(1).map((line) => ({
          id: line.id,
          attributes: line.attributes.filter((a) => a.key !== bespokeKey),
        })),
      );
    }
    if (withPropertySet.length === 0 && requiresShipping.length > 0) {
      // there are no lines with the bespokeKey, we need to set it on
      // the first line that requires shipping
      linesToUpdate.push({
        id: requiresShipping[0].id,
        attributes: [
          ...requiresShipping[0].attributes.filter(
            (attr) => attr.key !== bespokeKey,
          ),
          {key: bespokeKey, value: id},
        ],
      });
    }
    if (linesToUpdate.length > 0) {
      linesUpdate(linesToUpdate);
    }
  }, [id, lines, linesUpdate]);

  useEffect(() => {
    let cartCreateTimeout: NodeJS.Timeout;
    let discountCodesUpdateTimeout: NodeJS.Timeout;

    if (!id && status !== 'creating') {
      // setting a timeout of 1 second in hopes that the cart has
      // initialised as there is currently no way to know when it's
      // ready
      cartCreateTimeout = setTimeout(() => cartCreate({}), 1000);
    }
    if (
      id &&
      status == 'idle' &&
      discountCode &&
      discountCodes &&
      !discountCodes.find((v) => v.code === discountCode)
    ) {
      discountCodesUpdateTimeout = setTimeout(
        () => discountCodesUpdate([discountCode]),
        1000,
      );
    }

    if (
      discountCode &&
      discountCodes &&
      discountCodes.find((v) => v.code === discountCode)
    ) {
      unsetDiscountCode();
    }

    return () => {
      clearTimeout(cartCreateTimeout);
      clearTimeout(discountCodesUpdateTimeout);
    };
  }, [
    cartCreate,
    discountCode,
    discountCodes,
    discountCodesUpdate,
    id,
    status,
    unsetDiscountCode,
  ]);

  return children;
}
